import * as Cesium from 'cesium';
import { calculateZoomAltitude, formatArea } from './areaUtils';
import { groundLabelSample } from './labelAnchor';
// Where a text label's anchor sits on the surface that is active right now.
// The stored position keeps whatever height the placement pick returned
// (ellipsoid with 3D terrain off, terrain with it on); projecting that point
// after the terrain mode changes buries or floats the label and it slides
// against the ground as the camera moves. See utils/labelAnchor.ts.
export const groundedLabelPosition = (scene, position) => {
    const stored = Cesium.Cartographic.fromCartesian(position);
    if (!stored)
        return position;
    const grounded = groundLabelSample(stored, (longitude, latitude) => scene.globe.getHeight(new Cesium.Cartographic(longitude, latitude)));
    if (grounded === stored)
        return position;
    return Cesium.Cartesian3.fromRadians(grounded.longitude, grounded.latitude, grounded.height);
};
// Window position of a point on the active ground surface, in the same canvas
// coordinates a click there reports as event.position. Undefined when the
// point does not project or lands outside the canvas.
export const groundedWindowPosition = (viewer, position) => {
    if (viewer.isDestroyed())
        return undefined;
    const scene = viewer.scene;
    const projected = Cesium.SceneTransforms.worldToWindowCoordinates(scene, groundedLabelPosition(scene, position));
    if (!projected)
        return undefined;
    const { clientWidth, clientHeight } = scene.canvas;
    if (projected.x < 0 || projected.y < 0 || projected.x > clientWidth || projected.y > clientHeight)
        return undefined;
    return { x: projected.x, y: projected.y };
};
// Build a Cesium PolygonHierarchy from a polygon-with-holes. Used by path and
// stream buffer rendering so interior rings (holes — e.g. from a path that
// loops back on itself) render as actual holes instead of filling in solid.
export const buildBufferHierarchy = (poly) => {
    var _a;
    const toPositions = (coords) => coords.map(c => Cesium.Cartesian3.fromDegrees(c.lng, c.lat));
    const outer = toPositions(poly.exterior);
    const holes = ((_a = poly.holes) !== null && _a !== void 0 ? _a : [])
        .filter(h => h && h.length >= 3)
        .map(h => new Cesium.PolygonHierarchy(toPositions(h)));
    return new Cesium.PolygonHierarchy(outer, holes);
};
export const disableCameraControls = (viewer) => {
    const ctrl = viewer.scene.screenSpaceCameraController;
    ctrl.enableRotate = false;
    ctrl.enableTranslate = false;
    ctrl.enableZoom = false;
    ctrl.enableTilt = false;
    ctrl.enableLook = false;
};
export const enableCameraControls = (viewer) => {
    const ctrl = viewer.scene.screenSpaceCameraController;
    ctrl.enableRotate = true;
    ctrl.enableTranslate = true;
    ctrl.enableZoom = true;
    ctrl.enableTilt = true;
    ctrl.enableLook = true;
};
// Note: Using loose typing for protobuf-generated classes to avoid type export mismatches
/**
 * Display light blue project polygon
 * Uses the actual project polygon (requestedBounds)
 */
export const showProjectBoundsPolygon = (viewer, project) => {
    var _a;
    if (!viewer || viewer.isDestroyed()) {
        return;
    }
    // Remove any existing project bounds polygon
    viewer.entities.removeById('project-bounds-polygon');
    const requestedBoundsList = (_a = project.getRequestedBoundsList) === null || _a === void 0 ? void 0 : _a.call(project);
    if (!requestedBoundsList || requestedBoundsList.length < 3) {
        return;
    }
    const positions = requestedBoundsList.map((coord) => Cesium.Cartesian3.fromDegrees(coord.getLongitude(), coord.getLatitude()));
    viewer.entities.add({
        id: 'project-bounds-polygon',
        name: `${project.getName()} Bounds`,
        polyline: {
            positions: [...positions, positions[0]],
            width: 3,
            material: new Cesium.ColorMaterialProperty(Cesium.Color.BLUE),
            clampToGround: true
        }
    });
};
/**
 * Hide project bounds polygon
 */
export const hideProjectBoundsPolygon = (viewer) => {
    if (!viewer || viewer.isDestroyed())
        return;
    viewer.entities.removeById('project-bounds-polygon');
};
/**
 * Check if any artifacts are currently visible/loaded
 */
export const hasVisibleArtifacts = (artifacts, loadedArtifacts, isArtifactVisible) => {
    return artifacts.some(artifact => loadedArtifacts.has(artifact.getId()) && isArtifactVisible(artifact.getId()));
};
/**
 * Fly to and display a user project polygon on the map
 */
export const flyToUserProject = (viewer, userProject, areaUnit) => {
    if (!viewer || viewer.isDestroyed()) {
        return;
    }
    // Calculate averaged coordinate from coordinates
    if (userProject.requestedBounds.length >= 3) {
        const avgLat = userProject.requestedBounds.reduce((sum, coord) => sum + coord.lat, 0) / userProject.requestedBounds.length;
        const avgLng = userProject.requestedBounds.reduce((sum, coord) => sum + coord.lng, 0) / userProject.requestedBounds.length;
        // Calculate dynamic altitude based on polygon area
        const dynamicAltitude = calculateZoomAltitude(userProject.requestedAreaAcres || 0);
        try {
            // Fly to the averaged coordinate with dynamic altitude
            viewer.camera.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(avgLng, avgLat, dynamicAltitude),
                duration: 2.0,
                complete: () => {
                    // After flying, add the polygon entity
                    const positions = userProject.requestedBounds.map((coord) => Cesium.Cartesian3.fromDegrees(coord.lng, coord.lat));
                    // Remove any existing user project polygon and label
                    viewer.entities.removeById('user-project-polygon');
                    viewer.entities.removeById('user-project-label');
                    // Add the new polygon entity
                    viewer.entities.add({
                        id: 'user-project-polygon',
                        name: userProject.name,
                        polygon: {
                            hierarchy: positions,
                            material: Cesium.Color.BLUE.withAlpha(0.3),
                            outline: true,
                            outlineColor: Cesium.Color.BLUE,
                            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
                        }
                    });
                    // Add area label at the center
                    const areaText = formatArea(userProject.requestedAreaAcres || 0, areaUnit);
                    viewer.entities.add({
                        id: 'user-project-label',
                        position: Cesium.Cartesian3.fromDegrees(avgLng, avgLat),
                        label: {
                            text: areaText,
                            font: '21pt Arial',
                            fillColor: Cesium.Color.fromCssColorString('#f4f3f2'), // var(--sand)
                            outlineColor: Cesium.Color.fromCssColorString('#3e3f36'), // var(--charcoal)
                            outlineWidth: 2,
                            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
                            pixelOffset: new Cesium.Cartesian2(0, 0),
                            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                            scaleByDistance: new Cesium.NearFarScalar(500, 1.5, 15000, 0.75),
                            translucencyByDistance: new Cesium.NearFarScalar(50000, 1.0, 100000, 0.0),
                            disableDepthTestDistance: Number.POSITIVE_INFINITY
                        }
                    });
                }
            });
        }
        catch (error) {
            console.error('Error during flyTo:', error);
        }
    }
};
/**
 * Fly to a user project's area without adding any polygon or label.
 * Used when real data exists and bounds/layers will be shown instead.
 */
export const flyToUserProjectArea = (viewer, userProject) => {
    var _a;
    if (!viewer || viewer.isDestroyed()) {
        return;
    }
    if (((_a = userProject === null || userProject === void 0 ? void 0 : userProject.requestedBounds) === null || _a === void 0 ? void 0 : _a.length) >= 3) {
        const avgLat = userProject.requestedBounds.reduce((sum, coord) => sum + coord.lat, 0) / userProject.requestedBounds.length;
        const avgLng = userProject.requestedBounds.reduce((sum, coord) => sum + coord.lng, 0) / userProject.requestedBounds.length;
        const dynamicAltitude = calculateZoomAltitude(userProject.requestedAreaAcres || 0);
        try {
            viewer.camera.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(avgLng, avgLat, dynamicAltitude),
                duration: 2.0
            });
        }
        catch (_e) {
            // noop
        }
    }
};
/**
 * Update the area label for an existing user project polygon
 */
export const updateUserProjectLabel = (viewer, userProjects, areaUnit) => {
    var _a;
    if (!viewer || viewer.isDestroyed())
        return;
    const existingLabel = viewer.entities.getById('user-project-label');
    const existingPolygon = viewer.entities.getById('user-project-polygon');
    if (existingLabel && existingPolygon) {
        // Find the project data to update the label
        const labelPosition = (_a = existingLabel.position) === null || _a === void 0 ? void 0 : _a.getValue(viewer.clock.currentTime);
        if (labelPosition) {
            const cartographic = Cesium.Cartographic.fromCartesian(labelPosition);
            const avgLat = Cesium.Math.toDegrees(cartographic.latitude);
            const avgLng = Cesium.Math.toDegrees(cartographic.longitude);
            // Find the project with matching coordinates
            const matchingProject = userProjects.find(project => {
                const projectAvgLat = project.requestedBounds.reduce((sum, coord) => sum + coord.lat, 0) / project.requestedBounds.length;
                const projectAvgLng = project.requestedBounds.reduce((sum, coord) => sum + coord.lng, 0) / project.requestedBounds.length;
                return Math.abs(projectAvgLat - avgLat) < 0.001 && Math.abs(projectAvgLng - avgLng) < 0.001;
            });
            if (matchingProject) {
                const areaText = formatArea(matchingProject.requestedAreaAcres || 0, areaUnit);
                existingLabel.label.text = new Cesium.ConstantProperty(areaText);
            }
        }
    }
};
/**
 * Clear user project data from the map
 */
export const clearUserProjectData = (viewer) => {
    if (!viewer || viewer.isDestroyed()) {
        return;
    }
    // Remove user project polygon and label entities
    viewer.entities.removeById('user-project-polygon');
    viewer.entities.removeById('user-project-label');
};
/**
 * Clear all map content for drawing mode - removes artifacts, project bounds, and user projects
 */
export const clearMapForDrawingMode = (viewer, clearAllArtifacts) => {
    if (!viewer || viewer.isDestroyed()) {
        return;
    }
    // Clear all loaded artifacts (raster, vector, placemarks)
    clearAllArtifacts();
    // Clear user project data
    clearUserProjectData(viewer);
    // Clear project bounds polygon
    hideProjectBoundsPolygon(viewer);
    // Clear any other custom entities that might be on the map
    viewer.entities.removeById('project-bounds-polygon');
};
